import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { fadeUp, viewportOnce } from "./anim";
import { videoEmbedUrl } from "@/data/resume";

const VIDEO_TITLE = "Customer Success in the Age of AI — Webinar";

// Derive the YouTube poster image from the embed URL so the thumbnail
// reflects the actual video content.
const getYouTubePoster = (url) => {
  if (!url) return null;
  const match = url.match(/(?:embed\/|v=|youtu\.be\/)([\w-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` : null;
};

const FALLBACK_BACKDROP =
  "https://images.unsplash.com/photo-1531482615713-2afd69097998?q=85&w=1600&auto=format&fit=crop";

export const VideoSection = () => {
  const [playing, setPlaying] = useState(false);
  const poster = getYouTubePoster(videoEmbedUrl) || FALLBACK_BACKDROP;

  return (
    <section
      id="film"
      data-testid="video-section"
      className="relative mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-36"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-[#9A7526]">
            (02) — In my words
          </span>
          <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-[#1A1A18] md:text-6xl">
            Beyond Technology: <span className="italic text-[#9A7526]">Reimagining Customer Success with AI</span>
          </h2>
        </div>
        <p className="max-w-sm font-body text-sm font-light leading-relaxed text-[#6E6D66]">
          In this video, I share my perspective and reflections on customer
          success, the lessons that have shaped my career, and the
          opportunities AI creates for deeper customer impact.
        </p>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        data-testid="video-frame"
        className="group relative aspect-video w-full overflow-hidden border border-black/10 bg-[#0d0d0d]"
      >
        {videoEmbedUrl && playing ? (
          <iframe
            src={`${videoEmbedUrl}?autoplay=1`}
            title={VIDEO_TITLE}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            onClick={() => videoEmbedUrl && setPlaying(true)}
            data-testid="video-play-button"
            className="relative block h-full w-full"
          >
            <img
              src={poster}
              alt={VIDEO_TITLE}
              onError={(e) => {
                if (!e.currentTarget.dataset.fb) {
                  e.currentTarget.dataset.fb = "1";
                  const m = (videoEmbedUrl || "").match(/(?:embed\/|v=|youtu\.be\/)([\w-]{11})/);
                  e.currentTarget.src = m
                    ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`
                    : FALLBACK_BACKDROP;
                }
              }}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            {/* Scrim for legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />

            {/* Play control */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/60 bg-white/10 backdrop-blur-sm transition-colors duration-300 group-hover:border-[#9A7526] group-hover:bg-[#9A7526]">
                <Play size={24} className="ml-1 text-white" fill="currentColor" />
              </span>
              <span className="mt-6 font-mono-x text-[11px] uppercase tracking-[0.3em] text-white/80">
                {videoEmbedUrl ? "Play webinar" : "Video coming soon"}
              </span>
            </div>

            {/* Title caption */}
            <div
              data-testid="video-title"
              className="absolute bottom-0 left-0 w-full p-6 text-left md:p-8"
            >
              <span className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-[#e5c98a]">
                Webinar
              </span>
              <div className="mt-2 font-display text-2xl font-light leading-tight text-white md:text-4xl">
                Customer Success in the Age of AI
              </div>
            </div>
          </button>
        )}
      </motion.div>

      {/* Instructions for the owner to swap in their own video */}
    </section>
  );
};
